import { Controller } from "@hotwired/stimulus"
import { set as storeSet, subscribe } from "../lib/session_store"

export default class extends Controller {
  static targets = ["row"]
  static values = { initialLap: String }

  connect() {
    this._handleKeydown = (e) => this._onKeydown(e)
    window.addEventListener("keydown", this._handleKeydown)

    this._unsubs = [
      subscribe("activeLapNumber", (lapNumber) =>
        this._onStoreLapChange(lapNumber),
      ),
    ]

    if (this.hasInitialLapValue && this.initialLapValue) {
      const row = this.rowTargets.find(
        (r) => r.dataset.lapId === this.initialLapValue,
      )
      if (row) {
        this.activateLap(this.initialLapValue)
        row.scrollIntoView({ block: "center", behavior: "smooth" })
      } else if (this.rowTargets.length > 0) {
        this.activateLap(this.rowTargets[0].dataset.lapId)
      }
    } else if (this.rowTargets.length > 0) {
      this.activateLap(this.rowTargets[0].dataset.lapId)
    }
  }

  disconnect() {
    this._unsubs?.forEach((fn) => fn())
    window.removeEventListener("keydown", this._handleKeydown)
  }

  rowTargetConnected(element) {
    element.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  select(event) {
    // Don't navigate when clicking the checkbox
    if (event.target.closest('input[type="checkbox"]')) return

    const row = event.currentTarget
    const lapId = row.dataset.lapId

    // Guard: prevent _onStoreLapChange from also navigating while select handles it
    this._navigating = true
    try {
      if (lapId) this.activateLap(lapId, { userInitiated: event.isTrusted })
    } finally {
      this._navigating = false
    }

    // If click was already on a turbo-frame link, Turbo handles navigation
    if (event.target.closest("a[data-turbo-frame]")) return

    // Otherwise, programmatically click the first link in the row
    const link = row.querySelector("a[data-turbo-frame]")
    if (link) link.click()
  }

  selectByLapNumber(lapNumber) {
    if (!this._isVisible()) return
    const lapNum = String(
      typeof lapNumber === "object" ? lapNumber.detail?.lapNumber : lapNumber,
    )
    const row = this.rowTargets.find((r) => r.dataset.lapNumber === lapNum)
    if (!row) return

    // Don't re-navigate if already on this lap
    if (row.classList.contains("active")) return

    this.activateLap(row.dataset.lapId)

    const link =
      row.querySelector("a[data-turbo-frame]") ||
      row.closest("a[data-turbo-frame]")
    if (link) link.click()

    row.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  activateLap(lapId, { userInitiated = false } = {}) {
    this.rowTargets.forEach((r) => {
      if (r.dataset.lapId === lapId) {
        r.classList.add("active")
        // Write to store on direct user interaction so other controllers react
        if (userInitiated) {
          const lapNumber = r.dataset.lapNumber
          if (lapNumber) {
            storeSet("activeLapNumber", Number(lapNumber))
          }
        }
      } else {
        r.classList.remove("active")
      }
    })
  }

  // Called when another controller (e.g. pit-wall/replay) writes activeLapNumber to the store.
  // Skip if this controller is already mid-navigation (select/keydown) to prevent competing clicks.
  _onStoreLapChange(lapNumber) {
    if (this._navigating) return
    if (!this._isVisible()) return
    if (lapNumber == null) return
    this.selectByLapNumber(lapNumber)
  }

  // Highlight a row without Turbo Frame navigation (used by replay scrubbing).
  // Skip if a user click/keypress is in progress — the replay→pit-wall→highlightLap
  // chain fires synchronously and would overwrite the user's selection with lap N-1.
  highlightLapNumber(event) {
    if (this._navigating) return
    if (!this._isVisible()) return

    const lapNum = String(event.detail?.lapNumber)
    const row = this.rowTargets.find((r) => r.dataset.lapNumber === lapNum)
    if (!row || row.classList.contains("active")) return

    this.rowTargets.forEach((r) => r.classList.remove("active"))
    row.classList.add("active")
    row.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  _isVisible() {
    return this.element.offsetParent !== null
  }

  // --- Keyboard navigation ---

  _onKeydown(event) {
    // A modal command palette (or any open <dialog>) owns the keyboard while open.
    if (document.querySelector("dialog[open]")) return
    if (!this._isVisible()) return

    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return

    // Don't interfere with inputs/textareas
    const tag = document.activeElement?.tagName
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return
    if (document.activeElement?.isContentEditable) return

    event.preventDefault()

    const activeRow = this.rowTargets.find((r) =>
      r.classList.contains("active"),
    )
    if (!activeRow) return

    const currentIndex = this.rowTargets.indexOf(activeRow)
    const nextIndex =
      event.key === "ArrowUp" ? currentIndex - 1 : currentIndex + 1
    if (nextIndex < 0 || nextIndex >= this.rowTargets.length) return

    const nextRow = this.rowTargets[nextIndex]
    const lapId = nextRow.dataset.lapId
    if (!lapId) return

    this._navigating = true
    try {
      this.activateLap(lapId, { userInitiated: true })
    } finally {
      this._navigating = false
    }

    const link =
      nextRow.querySelector("a[data-turbo-frame]") ||
      nextRow.closest("a[data-turbo-frame]")
    if (link) link.click()

    nextRow.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }
}
